import {
  Alert,
  AppShell,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Code,
  Container,
  Group,
  Loader,
  SegmentedControl,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core"
import { notifications } from "@mantine/notifications"
import { IconAlertCircle, IconRefresh } from "@tabler/icons-react"
import { useQuery } from "@tanstack/react-query"
import { Option, Try } from "functype"
import { MatchOption } from "functype-react"
import { Fragment, useState } from "react"

import { Navbar } from "@/components/Navbar"
import { getServiceUrl, MONITORED_SERVICES, type ServiceId } from "@/config/services"
import { supabase } from "@/lib/supabase"

type EnvironmentTarget = "development" | "production"
type StatusFilter = "failed" | "all"

type JobRunRow = {
  id: string
  jobId: string
  jobType: string | null
  userId: string | null
  status: string
  startedAt: string
  completedAt: string | null
  durationMs: number | null
  errorMessage: string | null
  result: unknown
}

type JobRunsReport = {
  count: number
  status: StatusFilter
  rows: JobRunRow[]
}

const SERVICE_ID: ServiceId = "agent-gate"

const fetchJobRuns = async (target: EnvironmentTarget, status: StatusFilter): Promise<JobRunsReport> => {
  const { data, error } = await supabase.auth.getSession()
  if (error || !data.session) {
    throw new Error("Not signed in. Sign in with an admin account first.")
  }
  const token = data.session.access_token
  const url = getServiceUrl(SERVICE_ID, target === "production")
  const response = await fetch(`${url}/admin/job-runs?status=${status}&limit=50`, {
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  })
  if (!response.ok) {
    const text = await response.text()
    throw new Error(`Job runs fetch failed: ${response.status} ${response.statusText} ${text}`)
  }
  return response.json() as Promise<JobRunsReport>
}

const formatDuration = (ms: number | null): string => {
  if (ms === null) return "—"
  if (ms < 1000) return `${ms}ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`
  return `${(ms / 60_000).toFixed(1)}m`
}

const formatTimestamp = (iso: string): string => {
  const d = new Date(iso)
  return Number.isFinite(d.getTime()) ? d.toLocaleString() : iso
}

const renderStatusBadge = (status: string) => {
  const color = status === "completed" ? "green" : status === "failed" ? "red" : status === "running" ? "blue" : "gray"
  return (
    <Badge color={color} variant="light">
      {status}
    </Badge>
  )
}

const renderResultPreview = (result: unknown): string => {
  if (result === null || result === undefined) return ""
  return Try(() => JSON.stringify(result, null, 2)).fold(
    () => String(result),
    (v) => v,
  )
}

export function JobRunsPage() {
  const [target, setTarget] = useState<EnvironmentTarget>("development")
  const [status, setStatus] = useState<StatusFilter>("failed")
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null)

  const query = useQuery({
    queryKey: ["admin-job-runs", target, status],
    queryFn: () => fetchJobRuns(target, status),
    staleTime: 30_000,
    retry: 1,
  })

  const handleRefresh = () => {
    void query.refetch()
    notifications.show({ title: "Refreshing…", message: `Re-fetching ${target} job runs`, color: "blue" })
  }

  const targetUrl = MONITORED_SERVICES[SERVICE_ID][target === "production" ? "productionUrl" : "developmentUrl"]

  return (
    <AppShell padding="md">
      <Navbar onRefresh={handleRefresh} isRefreshing={query.isFetching} />

      <AppShell.Main>
        <Container size="xl" py="md">
          <Group justify="space-between" mb="lg">
            <Box>
              <Title order={2}>Job Runs</Title>
              <Text c="dimmed" size="sm">
                Recent rows from <Code>agent_schedule.job_runs</Code>. Default filter is failed-only for triage.
              </Text>
            </Box>
            <Group>
              <SegmentedControl
                value={status}
                onChange={(v) => setStatus(v as StatusFilter)}
                data={[
                  { label: "failed", value: "failed" },
                  { label: "all", value: "all" },
                ]}
              />
              <SegmentedControl
                value={target}
                onChange={(v) => setTarget(v as EnvironmentTarget)}
                data={[
                  { label: "dev", value: "development" },
                  { label: "prod", value: "production" },
                ]}
              />
              <Button
                leftSection={<IconRefresh size={16} />}
                onClick={handleRefresh}
                loading={query.isFetching}
                variant="default"
              >
                Refresh
              </Button>
            </Group>
          </Group>

          {query.isLoading && (
            <Center py="xl">
              <Box ta="center">
                <Loader size="lg" mb="md" />
                <Text>Loading job runs from {targetUrl}…</Text>
              </Box>
            </Center>
          )}

          {query.error && (
            <Alert color="red" icon={<IconAlertCircle size={16} />} title="Failed to load job runs">
              {query.error.message}
            </Alert>
          )}

          <MatchOption
            value={Option(query.data)}
            None={() => null}
            Some={(report) => (
              <Stack gap="md">
                <Card withBorder padding="md" radius="md">
                  <Group gap="xl">
                    <Box>
                      <Text size="xs" c="dimmed">
                        Filter
                      </Text>
                      <Text fw={600}>{report.status}</Text>
                    </Box>
                    <Box>
                      <Text size="xs" c="dimmed">
                        Rows returned
                      </Text>
                      <Text fw={600}>{report.count}</Text>
                    </Box>
                    <Box>
                      <Text size="xs" c="dimmed">
                        Backend URL
                      </Text>
                      <Code>{targetUrl}</Code>
                    </Box>
                  </Group>
                </Card>

                {report.rows.length === 0 ? (
                  <Card withBorder padding="md" radius="md">
                    <Text c="dimmed">No {report.status === "failed" ? "failed " : ""}runs found.</Text>
                  </Card>
                ) : (
                  <Card withBorder padding="md" radius="md">
                    <Table striped highlightOnHover withRowBorders={false}>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Started</Table.Th>
                          <Table.Th>Type</Table.Th>
                          <Table.Th>User</Table.Th>
                          <Table.Th>Status</Table.Th>
                          <Table.Th>Duration</Table.Th>
                          <Table.Th>Error</Table.Th>
                          <Table.Th></Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {report.rows.map((row) => {
                          const isExpanded = expandedRowId === row.id
                          return (
                            <Fragment key={row.id}>
                              <Table.Tr>
                                <Table.Td>
                                  <Text size="sm">{formatTimestamp(row.startedAt)}</Text>
                                </Table.Td>
                                <Table.Td>
                                  <Code>{row.jobType ?? "—"}</Code>
                                </Table.Td>
                                <Table.Td>
                                  <Code style={{ fontSize: 11 }}>
                                    {row.userId ? `${row.userId.slice(0, 8)}…` : "—"}
                                  </Code>
                                </Table.Td>
                                <Table.Td>{renderStatusBadge(row.status)}</Table.Td>
                                <Table.Td>
                                  <Text size="sm" c="dimmed">
                                    {formatDuration(row.durationMs)}
                                  </Text>
                                </Table.Td>
                                <Table.Td style={{ maxWidth: 360 }}>
                                  <Text size="sm" c={row.errorMessage ? "red" : "dimmed"} lineClamp={2}>
                                    {row.errorMessage ?? "—"}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  <Button
                                    size="xs"
                                    variant="subtle"
                                    onClick={() => setExpandedRowId(isExpanded ? null : row.id)}
                                  >
                                    {isExpanded ? "Hide" : "Details"}
                                  </Button>
                                </Table.Td>
                              </Table.Tr>
                              {isExpanded && (
                                <Table.Tr>
                                  <Table.Td colSpan={7}>
                                    <Stack gap="xs">
                                      <Group gap="xl">
                                        <Box>
                                          <Text size="xs" c="dimmed">
                                            Run id
                                          </Text>
                                          <Code style={{ fontSize: 11 }}>{row.id}</Code>
                                        </Box>
                                        <Box>
                                          <Text size="xs" c="dimmed">
                                            Job id
                                          </Text>
                                          <Code style={{ fontSize: 11 }}>{row.jobId}</Code>
                                        </Box>
                                        <Box>
                                          <Text size="xs" c="dimmed">
                                            User id
                                          </Text>
                                          <Code style={{ fontSize: 11 }}>{row.userId ?? "—"}</Code>
                                        </Box>
                                        <Box>
                                          <Text size="xs" c="dimmed">
                                            Completed
                                          </Text>
                                          <Text size="sm">
                                            {row.completedAt ? formatTimestamp(row.completedAt) : "—"}
                                          </Text>
                                        </Box>
                                      </Group>
                                      {row.errorMessage && (
                                        <Box>
                                          <Text size="xs" c="dimmed" mb={4}>
                                            Error message
                                          </Text>
                                          <Code block>{row.errorMessage}</Code>
                                        </Box>
                                      )}
                                      {row.result !== null && row.result !== undefined && (
                                        <Box>
                                          <Text size="xs" c="dimmed" mb={4}>
                                            Result
                                          </Text>
                                          <Code block style={{ maxHeight: 320, overflow: "auto" }}>
                                            {renderResultPreview(row.result)}
                                          </Code>
                                        </Box>
                                      )}
                                    </Stack>
                                  </Table.Td>
                                </Table.Tr>
                              )}
                            </Fragment>
                          )
                        })}
                      </Table.Tbody>
                    </Table>
                  </Card>
                )}
              </Stack>
            )}
          />
        </Container>
      </AppShell.Main>
    </AppShell>
  )
}
